import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ClipboardEvent, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LIMITI_ALLEGATI, type AllegatoCaricato, type TipoAllegato } from '@masamune/shared';
import { ApiError } from '../../lib/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAssistenteStore, type AllegatoInChat } from '../../stores/assistente.store';
import { CODICI_MUNE_FERMO } from './assistenteApi';
import { caricaAllegato, togliAllegato } from './allegatiApi';
import {
  BYTE_FOTO_PRONTA, BYTE_MINIATURA, BYTE_ORIGINALE, BYTE_PDF, PAROLE_ALLEGATI, annuncioPronto, caricamentoDefinitivo,
  cosaIncollare, fraseHeicComputer, fraseHeicTelefono, fraseInviaConErrore, fraseLungaStretta, frasePesoFoto,
  frasePesoPdf, fraseRete, fraseRovinato, fraseTipo, fraseTroppi, fraseTroppoGrandeDaAprire, genereDelFile,
  lettoreDaBlob, lungaEStretta, misureDaIntestazione, misureRidotte, nomeDaMostrare, nomeLibero, troppiPixel,
  type MisureFoto, type OrigineAllegato,
} from './allegatiTesto';

/**
 * Gli allegati nel campo della chat di MUNE (29/10/2026, fetta 2F «Allegati» della Fase 2;
 * os-planning/ai/fase-2f-allegati.md, «Interfaccia» e «Il web»). Niente JSX: i componenti stanno
 * in AllegatiCampo.tsx e AllegatiDomanda.tsx, le frasi in allegatiTesto.ts.
 *
 * Un file scelto con la graffetta, incollato o trascinato diventa un pezzo della striscia sopra il
 * campo. Una foto prima si controlla dall'intestazione (pixel, lungo e stretto) senza aprirla,
 * poi si ridisegna su un canvas: lato lungo al massimo 1568 px, JPEG 0,85 (uno screenshot PNG
 * resta PNG finché sta sotto 1 MB), senza i dati dello scatto e senza la posizione, più una
 * miniatura da 160 px. Un file alla volta si prepara; poi ognuno va da solo a POST /ai/allegati,
 * uno dopo l'altro, con la barra. La domanda porta solo gli id (`consegna`).
 *
 * I pezzi vivono nel pannello come il testo del campo, non nella conversazione: «Nuova
 * conversazione» e «Conversazioni» li lasciano dove sono. All'invio passano al fumetto della
 * domanda coi loro indirizzi blob:, che da lì libera la chat; se la domanda torna nel campo
 * (`rimetti`) tornano nella striscia così come sono. Al logout il pannello si smonta: i
 * caricamenti si interrompono e gli indirizzi della striscia si liberano; le righe rimaste in
 * attesa le toglie il server (24 ore, o il caricamento dopo della stessa persona).
 *
 * La chat costa e parte solo da Invio o dal tasto: qui niente parte da un effetto. La domanda
 * che aspetta un caricamento è la continuazione della promessa del clic (`consegna().pronti`);
 * gli indirizzi blob: nascono nei gestori degli eventi, mai negli effetti (StrictMode).
 */

/** Dove sta un pezzo nella striscia: in preparazione, in caricamento, pronto (ha l'id), in errore di rete. */
export type StatoPezzo = 'preparo' | 'carico' | 'pronto' | 'errore';

export interface AllegatoNelCampo extends AllegatoInChat {
  stato: StatoPezzo;
  /**
   * Bordo rosso su un pezzo già sul server: la domanda è tornata nel campo con un errore che lo
   * nomina (`details.allegati`: sparito, troppo pieno, non si legge).
   */
  sbagliato: boolean;
}

/** Una frase della riga rossa sotto la striscia, la più nuova prima. */
export interface RigaRossa {
  /** Per React e per togliRiga. */
  n: number;
  /** Il pezzo di cui parla; null per le frasi che non ne hanno uno (tipo, peso, troppi). */
  chiave: string | null;
  frase: string;
  /** Accanto il tasto «Riprova»: il pezzo è fermo per un errore di rete. */
  riprova: boolean;
  /** Accanto il tasto «Nuova conversazione» (ALLEGATI_CONVERSAZIONE). */
  nuova: boolean;
}

/** Com'è andata coi caricamenti della domanda mandata: gli id, o la frase per la riga rossa. */
export type EsitoPronti = { ok: true; ids: string[] } | { ok: false; frase: string };

export interface CampoAllegati {
  /** I pezzi della striscia, nell'ordine in cui sono entrati. */
  allegati: AllegatoNelCampo[];
  /** La riga rossa, la più nuova prima: al massimo tre frasi al computer, una al telefono. */
  errori: RigaRossa[];
  /**
   * L'ultima cosa da dire ai lettori di schermo: «Foto "x" allegata», «PDF "x" allegato: 12
   * pagine», le frasi della riga rossa. Il pannello la mette nella regione aria-live che ha già.
   */
  annuncio: string;
  /** Dalla graffetta, da Ctrl+V, dal trascinamento: i controlli, poi la preparazione e il caricamento. */
  aggiungi(file: File[], origine: OrigineAllegato): void;
  /**
   * La x: il pezzo sparisce subito. Un invio non finito si interrompe; uno finito si aspetta, e
   * con l'id parte DELETE /ai/allegati/:id senza aspettarne la risposta.
   */
  togli(chiave: string): void;
  /** «Riprova» su un pezzo fermo per un errore di rete. */
  riprova(chiave: string): void;
  /** Una frase nella riga rossa (Invia con un pezzo in errore: «Togli "x" o riprova a caricarlo…»). */
  avvisa(frase: string): void;
  /**
   * All'invio: la striscia si svuota, i caricamenti in corso continuano e aggiornano i pezzi della
   * domanda con `suPezzi`. `pronti` si risolve quando tutti sono sul server (gli id, nell'ordine
   * della striscia), o al primo che non ci arriva (la frase, e la domanda torna nel campo).
   */
  consegna(suPezzi: (p: AllegatoInChat[]) => void): { allegati: AllegatoInChat[]; pronti: Promise<EsitoPronti> };
  /**
   * Con ogni errore prima di `conversazione` («Domanda rimessa nel campo»): rimette i pezzi della
   * domanda in testa alla striscia, `sbagliati` (gli id di `details.allegati`) col bordo rosso, e
   * la frase nella riga rossa; con ALLEGATI_CONVERSAZIONE (`codice`) accanto il tasto «Nuova
   * conversazione». I pezzi falliti per un errore definitivo dopo l'invio non tornano.
   */
  rimetti(allegati: AllegatoInChat[], frase: string, sbagliati: string[], codice?: string | null): void;
  /** Toglie una frase dalla riga rossa (dopo «Nuova conversazione» non serve più). */
  togliRiga(n: number): void;
  /**
   * La domanda mandata è stata interrotta prima di salvarsi («Nuova conversazione», un'altra
   * conversazione da «Conversazioni»): non torna più nel campo, e i suoi pezzi si dimenticano.
   */
  abbandona(): void;
}

// ── Il campo, fuori da React ─────────────────────────────────────────────────

interface Pezzo {
  chiave: string;
  tipo: TipoAllegato;
  /** Un HEIC dal telefono: si apre senza misure dall'intestazione, e si controlla dopo. */
  heic: boolean;
  nome: string;
  /** Il file scelto: la foto da ridisegnare o il PDF. Null quando il pezzo non serve più. */
  file: Blob | null;
  /** Quello che parte verso il server: la foto ridisegnata o il PDF com'è. */
  pronto: Blob | null;
  miniatura: Blob | null;
  /** blob: della foto ridisegnata. */
  anteprima: string | null;
  id: string | null;
  pagine: number | null;
  stato: StatoPezzo;
  avanzamento: number | null;
  sbagliato: boolean;
  /** L'invio in corso. */
  ctrl: AbortController | null;
  /** Tutti i byte sono partiti (xhr.upload.onload): la x non interrompe più. */
  inviato: boolean;
  /** Tolto con la x, o buttato: quello che arriva dopo si ignora (o, con un id, si toglie dal server). */
  tolto: boolean;
  /** Errore definitivo dopo l'invio: non torna nella striscia. */
  scartato: boolean;
  /** Nella domanda mandata: il fumetto lo mostra, e la sua anteprima la libera la chat. */
  consegna: Consegna | null;
}

interface Consegna {
  pezzi: Pezzo[];
  suPezzi: (p: AllegatoInChat[]) => void;
  risolvi: (e: EsitoPronti) => void;
  finita: boolean;
  /** Il pezzo che l'ha fatta fallire per la rete: la sua frase, rimessa nel campo, ha accanto «Riprova». */
  motivo: { chiave: string; frase: string } | null;
}

interface Riga {
  n: number;
  chiave: string | null;
  frase: string;
  nuova: boolean;
}

interface VistaCampo {
  allegati: AllegatoNelCampo[];
  errori: RigaRossa[];
  annuncio: string;
}

let contatore = 0;
const nuovaChiave = () => `al${Date.now().toString(36)}${(contatore++).toString(36)}`;

function inChat(p: Pezzo): AllegatoInChat {
  return {
    chiave: p.chiave, id: p.id, tipo: p.tipo, nome: p.nome, pagine: p.pagine,
    anteprima: p.anteprima, avanzamento: p.avanzamento, letto: true,
  };
}

function nelCampo(p: Pezzo): AllegatoNelCampo {
  return { ...inChat(p), stato: p.stato, sbagliato: p.sbagliato };
}

/** I nomi degli allegati già nella conversazione aperta: un nome nuovo uguale prende « (2)». */
function nomiNellaConversazione(): string[] {
  return useAssistenteStore.getState().messaggi.flatMap((m) => (m.allegati ?? []).map((a) => a.nome));
}

/** Libera quello che il pezzo tiene, tranne l'anteprima quando è della chat (pezzo consegnato). */
function svuotaPezzo(p: Pezzo, anteprimaMia: boolean) {
  if (anteprimaMia && p.anteprima) URL.revokeObjectURL(p.anteprima);
  if (anteprimaMia) p.anteprima = null;
  p.file = null;
  p.pronto = null;
  p.miniatura = null;
}

class Campo {
  private striscia: Pezzo[] = [];
  private righe: Riga[] = [];
  private codaPreparo: Pezzo[] = [];
  private codaCarico: Pezzo[] = [];
  private preparando = false;
  private caricando = false;
  /** L'ultima domanda mandata coi suoi pezzi: l'unica che può tornare nel campo. */
  private consegnata: Consegna | null = null;
  private ascolta = new Set<() => void>();
  private nRiga = 0;
  private annuncio = '';
  private vivo = true;
  private vista: VistaCampo = { allegati: [], errori: [], annuncio: '' };
  mobile: boolean;
  /** MUNE spento o fermo per i tetti (CODICI_MUNE_FERMO): il pannello rilegge lo stato. */
  quandoFermo: (() => void) | null = null;

  constructor(mobile: boolean) {
    this.mobile = mobile;
  }

  // ── Per React (useSyncExternalStore) ──

  iscrivi = (l: () => void) => {
    this.ascolta.add(l);
    return () => { this.ascolta.delete(l); };
  };

  istantanea = () => this.vista;

  monta = () => { this.vivo = true; };

  /**
   * Il pannello si smonta (logout, ruolo di prova): gli invii si interrompono e gli indirizzi
   * della striscia si liberano. Niente chiamate: la sessione può essere già chiusa, e le righe
   * in attesa le toglie il server.
   */
  smonta = () => {
    this.vivo = false;
    const consegnati = this.consegnata?.pezzi ?? [];
    for (const p of [...this.striscia, ...consegnati]) {
      p.tolto = true;
      p.ctrl?.abort();
    }
    for (const p of this.striscia) svuotaPezzo(p, true);
    if (this.consegnata) this.fallisci(this.consegnata, '');
    this.striscia = [];
    this.consegnata = null;
    this.righe = [];
    this.codaPreparo = [];
    this.codaCarico = [];
    this.annuncio = '';
    this.emetti();
  };

  /** Le misure del telefono sono cambiate (una frase rossa sola): si ridisegna. */
  ricalcola = () => this.emetti();

  private emetti() {
    const quante = this.mobile ? 1 : 3;
    const conRiprova = new Set<string>();
    const errori = this.righe.slice(0, quante).map((r): RigaRossa => {
      const p = r.chiave ? this.striscia.find((x) => x.chiave === r.chiave) : undefined;
      // «Riprova» una volta sola per pezzo, sulla frase più nuova che lo nomina.
      const riprova = !!p && p.stato === 'errore' && !!p.pronto && !p.ctrl && !conRiprova.has(p.chiave);
      if (riprova && p) conRiprova.add(p.chiave);
      return { n: r.n, chiave: r.chiave, frase: r.frase, riprova, nuova: r.nuova };
    });
    this.vista = { allegati: this.striscia.map(nelCampo), errori, annuncio: this.annuncio };
    for (const l of this.ascolta) l();
  }

  /** Un pezzo è cambiato: la striscia, e il fumetto della domanda se è già stato mandato. */
  private cambiato(p: Pezzo) {
    const c = p.consegna;
    if (c) c.suPezzi(c.pezzi.map(inChat));
    this.emetti();
  }

  /** Una frase in cima alla riga rossa; la stessa frase non si ripete. */
  private aggiungiRiga(frase: string, chiave: string | null = null, nuova = false) {
    if (!frase) return;
    this.righe = [{ n: ++this.nRiga, chiave, frase, nuova }, ...this.righe.filter((r) => r.frase !== frase)].slice(0, 8);
    this.annuncio = frase;
  }

  // ── I comandi ──

  aggiungi = (file: File[], origine: OrigineAllegato) => {
    if (!this.vivo || file.length === 0) return;
    // Le frasi degli errori definitivi spariscono al prossimo allegato; restano quelle dei pezzi
    // ancora fermi per la rete, con il loro «Riprova».
    this.righe = this.righe.filter((r) => r.chiave !== null && this.striscia.some((p) => p.chiave === r.chiave && p.stato === 'errore'));
    const adesso = new Date();
    const usati = new Set([...this.striscia.map((p) => p.nome), ...nomiNellaConversazione()]);
    const frasi: string[] = [];
    const fuori: string[] = [];
    const nuovi: Pezzo[] = [];
    for (const f of file) {
      const genere = genereDelFile(f);
      const nome = nomeDaMostrare(f, origine, adesso);
      if (genere === null) { frasi.push(fraseTipo(nome, this.mobile)); continue; }
      // HEIC solo dal telefono: al computer Edge e Chrome non lo aprono (scelta 15).
      if (genere === 'heic' && !this.mobile) { frasi.push(fraseHeicComputer(nome)); continue; }
      if (genere === 'pdf' && f.size > BYTE_PDF) { frasi.push(frasePesoPdf(nome, f.size)); continue; }
      if (genere !== 'pdf' && f.size > BYTE_ORIGINALE) { frasi.push(frasePesoFoto(nome, f.size)); continue; }
      const tipo: TipoAllegato = genere === 'pdf' ? 'pdf' : 'foto';
      if (f.size === 0) { frasi.push(fraseRovinato(nome, tipo)); continue; }
      if (this.striscia.length + nuovi.length >= LIMITI_ALLEGATI.perDomanda) { fuori.push(nome); continue; }
      const libero = nomeLibero(nome, usati);
      usati.add(libero);
      nuovi.push({
        chiave: nuovaChiave(), tipo, heic: genere === 'heic', nome: libero, file: f, pronto: tipo === 'pdf' ? f : null,
        miniatura: null, anteprima: null, id: null, pagine: null, stato: tipo === 'pdf' ? 'carico' : 'preparo',
        avanzamento: 0, sbagliato: false, ctrl: null, inviato: false, tolto: false, scartato: false, consegna: null,
      });
    }
    if (fuori.length) frasi.push(fraseTroppi(fuori));
    // Una frase per file, nell'ordine dei file, sopra quelle di prima.
    for (const frase of [...frasi].reverse()) this.aggiungiRiga(frase);
    if (nuovi.length) {
      this.striscia = [...this.striscia, ...nuovi];
      for (const p of nuovi) (p.tipo === 'pdf' ? this.codaCarico : this.codaPreparo).push(p);
    }
    this.emetti();
    void this.prossimaPreparazione();
    void this.prossimoCaricamento();
  };

  togli = (chiave: string) => {
    const p = this.striscia.find((x) => x.chiave === chiave);
    if (!p) return;
    this.striscia = this.striscia.filter((x) => x !== p);
    this.righe = this.righe.filter((r) => r.chiave !== chiave);
    this.butta(p);
    this.emetti();
  };

  riprova = (chiave: string) => {
    const p = this.striscia.find((x) => x.chiave === chiave);
    if (!p || p.stato !== 'errore' || !p.pronto || p.ctrl) return;
    this.righe = this.righe.filter((r) => r.chiave !== chiave);
    p.stato = 'carico';
    p.avanzamento = 0;
    p.sbagliato = false;
    if (!this.codaCarico.includes(p)) this.codaCarico.push(p);
    this.emetti();
    void this.prossimoCaricamento();
  };

  avvisa = (frase: string) => {
    // «Togli "x" o riprova a caricarlo…»: la frase nomina il pezzo fermo, e accanto ha il suo «Riprova».
    const rotto = this.striscia.find((p) => p.stato === 'errore' && frase === fraseInviaConErrore(p.nome));
    this.aggiungiRiga(frase, rotto?.chiave ?? null);
    this.emetti();
  };

  togliRiga = (n: number) => {
    this.righe = this.righe.filter((r) => r.n !== n);
    this.emetti();
  };

  consegna = (suPezzi: (p: AllegatoInChat[]) => void): { allegati: AllegatoInChat[]; pronti: Promise<EsitoPronti> } => {
    // La domanda di prima non torna più nel campo: i suoi pezzi si dimenticano.
    if (this.consegnata) this.dimentica(this.consegnata);
    const pezzi = this.striscia;
    this.striscia = [];
    // Le frasi spariscono all'invio.
    this.righe = [];
    this.annuncio = '';
    let risolvi: (e: EsitoPronti) => void = () => {};
    const pronti = new Promise<EsitoPronti>((ok) => { risolvi = ok; });
    const c: Consegna = { pezzi, suPezzi, risolvi, finita: false, motivo: null };
    this.consegnata = pezzi.length ? c : null;
    for (const p of pezzi) {
      p.consegna = c;
      p.sbagliato = false;
    }
    const rotto = pezzi.find((p) => p.stato === 'errore');
    if (rotto) this.fallisci(c, fraseInviaConErrore(rotto.nome), rotto.chiave);
    else this.seFinita(c);
    this.emetti();
    return { allegati: pezzi.map(inChat), pronti };
  };

  rimetti = (allegati: AllegatoInChat[], frase: string, sbagliati: string[], codice?: string | null) => {
    if (!this.vivo) return;
    const c = this.consegnata;
    this.consegnata = null;
    const tornano: Pezzo[] = [];
    if (c) {
      this.fallisci(c, frase);
      for (const p of c.pezzi) {
        if (p.consegna !== c) continue;
        p.consegna = null;
        if (p.scartato || p.tolto) {
          // Fallito per sempre dopo l'invio: non torna, e il fumetto che lo mostrava se ne va
          // senza liberare niente (togliMessaggi). L'indirizzo lo libera il campo.
          p.tolto = true;
          svuotaPezzo(p, true);
          continue;
        }
        p.sbagliato = p.id !== null && sbagliati.includes(p.id);
        tornano.push(p);
      }
    }
    // Un pezzo che il campo non conosce (il pannello rimontato nel frattempo): torna com'è, se è sul server.
    for (const a of allegati) {
      if (!a.id || c?.pezzi.some((p) => p.chiave === a.chiave)) continue;
      tornano.push({
        chiave: a.chiave, tipo: a.tipo, heic: false, nome: a.nome, file: null, pronto: null, miniatura: null,
        anteprima: a.anteprima, id: a.id, pagine: a.pagine, stato: 'pronto', avanzamento: null,
        sbagliato: sbagliati.includes(a.id), ctrl: null, inviato: false, tolto: false, scartato: false, consegna: null,
      });
    }
    // Lo stesso file allegato di nuovo mentre la domanda partiva (29/10/2026, dalla revisione): il
    // server ha dato la stessa riga, e due pezzi con lo stesso id darebbero «Lo stesso allegato due
    // volte». Resta quello che torna; l'altro va via senza DELETE, perché la riga è la stessa.
    const idTornano = new Set(tornano.flatMap((p) => (p.id ? [p.id] : [])));
    const doppi = this.striscia.filter((p) => !tornano.includes(p) && p.id !== null && idTornano.has(p.id));
    for (const p of doppi) {
      p.tolto = true;
      svuotaPezzo(p, true);
    }
    this.righe = this.righe.filter((r) => !doppi.some((p) => p.chiave === r.chiave));
    // In testa i pezzi della domanda; dopo quelli allegati mentre la domanda partiva. Oltre i 5
    // escono gli ultimi arrivati, con la frase di «Troppi».
    const insieme = [...tornano, ...this.striscia.filter((p) => !tornano.includes(p) && !doppi.includes(p))];
    const fuori = insieme.slice(LIMITI_ALLEGATI.perDomanda);
    this.striscia = insieme.slice(0, LIMITI_ALLEGATI.perDomanda);
    for (const p of fuori) this.butta(p);
    if (fuori.length) this.aggiungiRiga(fraseTroppi(fuori.map((p) => p.nome)));
    const rotto = c?.motivo && c.motivo.frase === frase && tornano.some((p) => p.chiave === c.motivo?.chiave && p.stato === 'errore')
      ? c.motivo.chiave : null;
    const nuova = codice === 'ALLEGATI_CONVERSAZIONE' || frase === PAROLE_ALLEGATI.troppiInConversazione;
    this.aggiungiRiga(frase, rotto, nuova);
    this.emetti();
  };

  /**
   * La domanda mandata è stata interrotta prima di salvarsi (29/10/2026, dalla revisione della 2F):
   * il pannello ha cambiato conversazione a metà. I suoi pezzi si dimenticano come quando ne parte
   * un'altra (dimentica): gli invii non finiti si interrompono, e quelli già partiti tolgono la riga
   * appena arriva l'id. Senza, la domanda abbandonata restava `consegnata`, e lo stesso file
   * allegato di nuovo trovava lì il suo gemello e spariva dalla striscia senza una frase.
   */
  abbandona = () => {
    const c = this.consegnata;
    if (!c) return;
    this.consegnata = null;
    this.dimentica(c);
    this.emetti();
  };

  // ── Dentro ──

  /** Il pezzo se ne va per sempre (la x, o oltre i 5): niente più aggiornamenti, e la riga sul server si toglie. */
  private butta(p: Pezzo) {
    p.tolto = true;
    this.codaPreparo = this.codaPreparo.filter((x) => x !== p);
    this.codaCarico = this.codaCarico.filter((x) => x !== p);
    if (p.ctrl) {
      // Un invio non finito si interrompe; uno finito si aspetta: con l'id, carica() toglie la riga.
      if (!p.inviato) p.ctrl.abort();
    } else if (p.id) {
      void togliAllegato(p.id).catch(() => {});
    }
    svuotaPezzo(p, true);
  }

  /**
   * I pezzi di una domanda che non può più tornare nel campo (ne parte un'altra): si dimenticano.
   * Le loro anteprime le tiene la chat. Un invio ancora in corso (la domanda interrotta con «Nuova
   * conversazione») si ferma come con la x.
   */
  private dimentica(c: Consegna) {
    this.fallisci(c, '');
    for (const p of c.pezzi) {
      if (p.consegna !== c) continue;
      p.consegna = null;
      p.tolto = true;
      this.codaPreparo = this.codaPreparo.filter((x) => x !== p);
      this.codaCarico = this.codaCarico.filter((x) => x !== p);
      if (p.ctrl && !p.inviato) p.ctrl.abort();
      svuotaPezzo(p, false);
    }
  }

  private fallisci(c: Consegna, frase: string, chiave?: string) {
    if (c.finita) return;
    c.finita = true;
    if (chiave) c.motivo = { chiave, frase };
    c.risolvi({ ok: false, frase });
  }

  /** Tutti i pezzi della domanda sono sul server: la domanda parte. */
  private seFinita(c: Consegna | null) {
    if (!c || c.finita || !c.pezzi.every((p) => p.id !== null)) return;
    c.finita = true;
    const ids = [...new Set(c.pezzi.flatMap((p) => (p.id ? [p.id] : [])))];
    c.risolvi({ ok: true, ids });
  }

  /** Errore definitivo (tipo, peso, pagine, password, rovinato): il pezzo non resta, la frase sì. */
  private scarta(p: Pezzo, frase: string) {
    p.stato = 'errore';
    p.avanzamento = null;
    p.ctrl = null;
    if (p.consegna) {
      p.scartato = true;
      this.fallisci(p.consegna, frase);
      this.cambiato(p);
      return;
    }
    p.tolto = true;
    this.striscia = this.striscia.filter((x) => x !== p);
    this.righe = this.righe.filter((r) => r.chiave !== p.chiave);
    svuotaPezzo(p, true);
    this.aggiungiRiga(frase);
    this.emetti();
  }

  /** Errore di rete, o il server che non risponde: il pezzo resta col bordo rosso e «Riprova». */
  private inErrore(p: Pezzo, frase: string) {
    p.stato = 'errore';
    p.avanzamento = null;
    p.ctrl = null;
    if (p.consegna) {
      this.fallisci(p.consegna, frase, p.chiave);
      this.cambiato(p);
      return;
    }
    this.aggiungiRiga(frase, p.chiave);
    this.emetti();
  }

  // ── La preparazione delle foto, una alla volta ──

  private async prossimaPreparazione(): Promise<void> {
    if (this.preparando || !this.vivo) return;
    const p = this.codaPreparo.shift();
    if (!p) return;
    this.preparando = true;
    try {
      await this.prepara(p);
    } catch {
      // prepara() dice da sé cosa non va; qui non si ferma la coda.
    } finally {
      this.preparando = false;
    }
    void this.prossimaPreparazione();
  }

  private async prepara(p: Pezzo): Promise<void> {
    const file = p.file;
    const via = () => p.tolto || !this.vivo;
    if (via() || !file) return;
    let misure: MisureFoto | null = null;
    if (!p.heic) {
      // Prima di aprirla, dall'intestazione: una foto da 30.000 × 30.000 aperta chiude la scheda.
      try { misure = await misureDaIntestazione(lettoreDaBlob(file), file.size); } catch { misure = null; }
      if (via()) return;
      if (!misure) return this.scarta(p, fraseRovinato(p.nome, 'foto'));
      if (troppiPixel(misure)) return this.scarta(p, fraseTroppoGrandeDaAprire(p.nome));
      if (lungaEStretta(misure)) return this.scarta(p, fraseLungaStretta(p.nome));
    }
    let foto: FotoPronta;
    try {
      foto = await ridisegna(file, { png: misure?.formato === 'png', controlla: p.heic, via });
    } catch (e) {
      if (via()) return;
      const perche = e instanceof FotoRifiutata ? e.perche : 'apre';
      return this.scarta(p, perche === 'pixel' ? fraseTroppoGrandeDaAprire(p.nome)
        : perche === 'stretta' ? fraseLungaStretta(p.nome)
        : p.heic ? fraseHeicTelefono(p.nome)
        : fraseRovinato(p.nome, 'foto'));
    }
    if (via()) return;
    p.pronto = foto.foto;
    p.miniatura = foto.miniatura;
    // L'indirizzo nasce qui, nel giro partito dal gestore dell'evento, e lo libera chi butta il pezzo.
    p.anteprima = URL.createObjectURL(foto.foto);
    p.file = null;
    p.stato = 'carico';
    p.avanzamento = 0;
    this.codaCarico.push(p);
    this.cambiato(p);
    void this.prossimoCaricamento();
  }

  // ── I caricamenti, uno dopo l'altro ──

  private async prossimoCaricamento(): Promise<void> {
    if (this.caricando || !this.vivo) return;
    const p = this.codaCarico.shift();
    if (!p) return;
    this.caricando = true;
    try {
      await this.carica(p);
    } catch {
      // carica() dice da sé cosa non va; qui non si ferma la coda.
    } finally {
      this.caricando = false;
    }
    void this.prossimoCaricamento();
  }

  private async carica(p: Pezzo): Promise<void> {
    const file = p.pronto;
    if (p.tolto || !file) return;
    const ctrl = new AbortController();
    p.ctrl = ctrl;
    p.inviato = false;
    p.stato = 'carico';
    p.avanzamento = 0;
    this.cambiato(p);
    let r: AllegatoCaricato;
    try {
      r = await caricaAllegato({
        file, nome: p.nome, miniatura: p.miniatura, signal: ctrl.signal, tipo: p.tipo, mobile: this.mobile,
        onAvanzamento: (x) => this.avanza(p, x),
        onInviato: () => { p.inviato = true; },
      });
    } catch (e) {
      p.ctrl = null;
      if (p.tolto || !this.vivo || ctrl.signal.aborted) return;
      const errore = e instanceof ApiError ? e : null;
      if (errore && CODICI_MUNE_FERMO.has(errore.code)) this.quandoFermo?.();
      const frase = errore?.message || fraseRete(p.nome);
      if (errore && caricamentoDefinitivo(errore.status, errore.code)) this.scarta(p, frase);
      else this.inErrore(p, frase);
      return;
    }
    p.ctrl = null;
    p.inviato = false;
    // Tolto con la x mentre la risposta arrivava: la riga sul server non serve più.
    if (p.tolto) { void togliAllegato(r.id).catch(() => {}); return; }
    if (!this.vivo) return;
    // Lo stesso file due volte: il server rende la riga che ha già (stesso sha256). Il pezzo
    // nuovo sparisce senza frase: con due id uguali la domanda darebbe «Lo stesso allegato due
    // volte», e la x di uno toglierebbe la riga dell'altro.
    if (this.gemello(p, r.id)) { this.togliGemello(p); return; }
    p.id = r.id;
    p.pagine = r.pagine;
    p.nome = r.nome || p.nome;
    p.stato = 'pronto';
    p.avanzamento = null;
    if (!p.consegna) this.annuncio = annuncioPronto(p);
    this.cambiato(p);
    this.seFinita(p.consegna);
  }

  /**
   * Un altro pezzo vivo, nella striscia o nella domanda che sta partendo, ha già quell'id. I pezzi
   * della domanda contano solo mentre aspetta i caricamenti (29/10/2026, dalla revisione): partita
   * la domanda, lo stesso file allegato di nuovo è per quella dopo e resta nella striscia. Se la
   * domanda poi torna nel campo, rimetti tiene un pezzo solo; se si salva, la riga è sua e il pezzo
   * dopo, mandato, prende «non c'è più: toglilo e allegalo di nuovo».
   */
  private gemello(p: Pezzo, id: string): boolean {
    const c = this.consegnata;
    const vicini = [...this.striscia, ...(c && !c.finita ? c.pezzi : [])];
    return vicini.some((x) => x !== p && !x.tolto && x.id === id);
  }

  private togliGemello(p: Pezzo) {
    p.tolto = true;
    const c = p.consegna;
    if (c) {
      p.consegna = null;
      c.pezzi = c.pezzi.filter((x) => x !== p);
      c.suPezzi(c.pezzi.map(inChat));
      this.seFinita(c);
    } else {
      this.striscia = this.striscia.filter((x) => x !== p);
      this.righe = this.righe.filter((r) => r.chiave !== p.chiave);
    }
    // La riga sul server è del gemello: niente DELETE. L'anteprima, tolta dal fumetto, la libera il campo.
    svuotaPezzo(p, true);
    this.emetti();
  }

  /** La barra: un aggiornamento ogni 2 % basta (ogni aggiornamento ridisegna anche il fumetto della domanda). */
  private avanza(p: Pezzo, x: number) {
    if (p.tolto || p.stato !== 'carico') return;
    const prima = p.avanzamento ?? 0;
    if (x < 1 && x - prima < 0.02) return;
    p.avanzamento = Math.min(1, Math.max(0, x));
    this.cambiato(p);
  }
}

// ── Ridisegnare una foto ─────────────────────────────────────────────────────

interface FotoPronta {
  foto: Blob;
  miniatura: Blob | null;
}

/** Perché una foto non si prepara: non si apre, troppi pixel, troppo lunga e stretta. */
class FotoRifiutata extends Error {
  constructor(public perche: 'apre' | 'pixel' | 'stretta') {
    super(perche);
  }
}

/** Le qualità del JPEG, dalla prima: 0,85 come da progetto; la seconda solo se la prima passa 1 MB. */
const QUALITA_FOTO = [0.85, 0.72];
const QUALITA_MINIATURA = [0.7, 0.55];

function caricata(img: HTMLImageElement): Promise<void> {
  return new Promise((ok, no) => {
    img.onload = () => ok();
    img.onerror = () => no(new FotoRifiutata('apre'));
  });
}

function tela(img: HTMLImageElement, m: { larghezza: number; altezza: number }, bianco: boolean): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = m.larghezza;
  c.height = m.altezza;
  const ctx = c.getContext('2d');
  if (!ctx) { svuotaTela(c); throw new FotoRifiutata('apre'); }
  // Un PNG trasparente in JPEG diventerebbe nero: prima il bianco.
  if (bianco) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, m.larghezza, m.altezza);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, m.larghezza, m.altezza);
  return c;
}

const inBlob = (c: HTMLCanvasElement, tipo: string, qualita?: number) =>
  new Promise<Blob | null>((ok) => c.toBlob(ok, tipo, qualita));

/** Safari ha un tetto alla memoria dei canvas: ogni tela si svuota appena usata. */
function svuotaTela(c: HTMLCanvasElement) {
  c.width = 0;
  c.height = 0;
}

/**
 * La foto pronta per il server, al massimo 1 MB. Uno screenshot PNG resta PNG se ci sta (il testo
 * resta nitido); se no JPEG 0,85, poi 0,72; se ancora non ci sta, la foto si stringe di un quinto
 * e si riprova. `toBlob` dà un PNG senza dirlo quando il formato chiesto non c'è: si guarda il tipo.
 */
async function fotoLeggera(img: HTMLImageElement, larghezza: number, altezza: number, png: boolean): Promise<Blob> {
  let m = misureRidotte(larghezza, altezza);
  if (png) {
    const c = tela(img, m, false);
    try {
      const b = await inBlob(c, 'image/png');
      if (b && b.type === 'image/png' && b.size <= BYTE_FOTO_PRONTA) return b;
    } finally {
      svuotaTela(c);
    }
  }
  for (let giro = 0; giro < 4; giro++) {
    const c = tela(img, m, true);
    try {
      for (const q of QUALITA_FOTO) {
        const b = await inBlob(c, 'image/jpeg', q);
        if (b && b.type === 'image/jpeg' && b.size <= BYTE_FOTO_PRONTA) return b;
      }
    } finally {
      svuotaTela(c);
    }
    m = { larghezza: Math.max(1, Math.round(m.larghezza * 0.8)), altezza: Math.max(1, Math.round(m.altezza * 0.8)) };
  }
  throw new FotoRifiutata('apre');
}

/** La miniatura da 160 px, JPEG 0,7, al massimo 12 KB; se non ci sta, niente miniatura (il server la butterebbe). */
async function miniaturaDi(img: HTMLImageElement, larghezza: number, altezza: number): Promise<Blob | null> {
  const c = tela(img, misureRidotte(larghezza, altezza, LIMITI_ALLEGATI.latoMiniatura), true);
  try {
    for (const q of QUALITA_MINIATURA) {
      const b = await inBlob(c, 'image/jpeg', q);
      if (b && b.type === 'image/jpeg' && b.size <= BYTE_MINIATURA) return b;
    }
    return null;
  } finally {
    svuotaTela(c);
  }
}

/**
 * Apre la foto con un <img> (dal 2020 tutti i browser la girano secondo l'EXIF quando la
 * disegnano) e la ridisegna: ridisegnare toglie i dati dello scatto, compresa la posizione GPS.
 * Un HEIC (`controlla`) non ha misure dall'intestazione: pixel e proporzioni si guardano qui.
 */
async function ridisegna(file: Blob, o: { png: boolean; controlla: boolean; via: () => boolean }): Promise<FotoPronta> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  try {
    img.src = url;
    try {
      await (typeof img.decode === 'function' ? img.decode() : caricata(img));
    } catch {
      throw new FotoRifiutata('apre');
    }
    const larghezza = img.naturalWidth;
    const altezza = img.naturalHeight;
    if (!larghezza || !altezza) throw new FotoRifiutata('apre');
    if (o.controlla) {
      if (troppiPixel({ larghezza, altezza })) throw new FotoRifiutata('pixel');
      if (lungaEStretta({ larghezza, altezza })) throw new FotoRifiutata('stretta');
    }
    if (o.via()) throw new FotoRifiutata('apre');
    const foto = await fotoLeggera(img, larghezza, altezza, o.png);
    const miniatura = await miniaturaDi(img, larghezza, altezza);
    return { foto, miniatura };
  } finally {
    img.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
}

// ── L'hook del pannello ──────────────────────────────────────────────────────

/**
 * Il campo degli allegati del pannello di MUNE. Un'istanza per pannello montato: vive quanto il
 * pannello, come il testo del campo.
 */
export function useAllegatiCampo(): CampoAllegati {
  const mobile = useIsMobile();
  const qc = useQueryClient();
  const [campo] = useState(() => new Campo(mobile));
  useEffect(() => {
    campo.mobile = mobile;
    campo.quandoFermo = () => { void qc.invalidateQueries({ queryKey: ['ai', 'stato'] }); };
    campo.ricalcola();
  }, [campo, mobile, qc]);
  useEffect(() => {
    campo.monta();
    return campo.smonta;
  }, [campo]);
  const vista = useSyncExternalStore(campo.iscrivi, campo.istantanea);
  return useMemo(() => ({
    allegati: vista.allegati,
    errori: vista.errori,
    annuncio: vista.annuncio,
    aggiungi: campo.aggiungi,
    togli: campo.togli,
    riprova: campo.riprova,
    avvisa: campo.avvisa,
    consegna: campo.consegna,
    rimetti: campo.rimetti,
    togliRiga: campo.togliRiga,
    abbandona: campo.abbandona,
  }), [vista, campo]);
}

// ── Incollare, trascinare, la guardia della finestra ─────────────────────────

/** I file degli appunti: `files`, o gli `items` di tipo file dove `files` resta vuoto. */
function fileDegliAppunti(d: DataTransfer): File[] {
  const dai = Array.from(d.files ?? []);
  if (dai.length) return dai;
  const fuori: File[] = [];
  for (const it of Array.from(d.items ?? [])) {
    if (it.kind !== 'file') continue;
    const f = it.getAsFile();
    if (f) fuori.push(f);
  }
  return fuori;
}

/**
 * Ctrl+V nel campo (onPaste). Con del testo negli appunti si incolla il testo come oggi e
 * l'immagine si lascia (Excel e Word copiano anche un'immagine delle celle); senza testo, i file
 * diventano allegati. `preventDefault` solo quando li prende, come incollaPiuRighe
 * (features/tasks/tasksApi.ts). Null: l'incolla resta quello di sempre.
 */
export function fileDaIncollare(e: ClipboardEvent<HTMLTextAreaElement>): File[] | null {
  const d = e.clipboardData;
  if (!d) return null;
  const file = fileDegliAppunti(d);
  if (cosaIncollare(d.getData('text'), file.length) !== 'file') return null;
  e.preventDefault();
  return file;
}

/** Il trascinamento porta dei file (non una card della Kanban, che porta text/plain). */
const conFile = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');

/** Senza un `dragover` per 300 ms il riquadro sparisce: un file preso dal computer non manda `dragend` alla pagina. */
const SILENZIO_MS = 300;

/**
 * Trascinare foto e PDF sulla `zona` (al computer, tutto il pannello sotto l'intestazione).
 * Restituisce true mentre sopra passa un file: il pannello mostra RilascioAllegati. Entrate e
 * uscite si contano (dragenter e dragleave scattano anche sui figli), e il riquadro sparisce
 * anche dopo 300 ms senza `dragover` e quando la finestra perde il fuoco, come lib/dnd.ts.
 * Spento (`attivo` falso: al telefono, o con MUNE non disponibile) non ascolta niente.
 */
export function useTrascinaFile(zona: RefObject<HTMLElement>, attivo: boolean, onFile: (f: File[]) => void): boolean {
  const [sopra, setSopra] = useState(false);
  const daChiamare = useRef(onFile);
  useEffect(() => { daChiamare.current = onFile; });
  useEffect(() => {
    const el = zona.current;
    if (!attivo || !el) { setSopra(false); return; }
    let dentro = 0;
    let silenzio = 0;
    const via = () => {
      dentro = 0;
      window.clearTimeout(silenzio);
      setSopra(false);
    };
    const ancora = () => {
      window.clearTimeout(silenzio);
      silenzio = window.setTimeout(via, SILENZIO_MS);
    };
    const entra = (e: DragEvent) => {
      if (!conFile(e)) return;
      e.preventDefault();
      dentro++;
      setSopra(true);
      ancora();
    };
    const passa = (e: DragEvent) => {
      if (!conFile(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setSopra(true);
      ancora();
    };
    const esce = (e: DragEvent) => {
      if (!conFile(e)) return;
      dentro = Math.max(0, dentro - 1);
      if (dentro === 0) via();
    };
    const lascia = (e: DragEvent) => {
      if (!conFile(e)) return;
      e.preventDefault();
      const file = Array.from(e.dataTransfer?.files ?? []);
      via();
      if (file.length) daChiamare.current(file);
    };
    el.addEventListener('dragenter', entra);
    el.addEventListener('dragover', passa);
    el.addEventListener('dragleave', esce);
    el.addEventListener('drop', lascia);
    window.addEventListener('blur', via);
    window.addEventListener('dragend', via);
    window.addEventListener('drop', via);
    return () => {
      el.removeEventListener('dragenter', entra);
      el.removeEventListener('dragover', passa);
      el.removeEventListener('dragleave', esce);
      el.removeEventListener('drop', lascia);
      window.removeEventListener('blur', via);
      window.removeEventListener('dragend', via);
      window.removeEventListener('drop', via);
      window.clearTimeout(silenzio);
      setSopra(false);
    };
  }, [zona, attivo]);
  return attivo && sopra;
}

/**
 * A pannello aperto, un file lasciato fuori dalla zona non si apre al posto dell'app (oggi
 * succede in tutto il gestionale): `dragover` e `drop` di file che nessuno ha gestito
 * (`defaultPrevented` falso) si fermano, col cursore del «qui no». Chi li gestisce da sé
 * (UploadDocModal) chiama preventDefault prima, e la Kanban non porta file.
 */
export function useGuardiaFile(attiva: boolean): void {
  useEffect(() => {
    if (!attiva) return;
    const passa = (e: DragEvent) => {
      if (e.defaultPrevented || !conFile(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    const lascia = (e: DragEvent) => {
      if (e.defaultPrevented || !conFile(e)) return;
      e.preventDefault();
    };
    window.addEventListener('dragover', passa);
    window.addEventListener('drop', lascia);
    return () => {
      window.removeEventListener('dragover', passa);
      window.removeEventListener('drop', lascia);
    };
  }, [attiva]);
}
